import { type OneDriveFile } from '../../OneDriveFilePicker/FilePicker/types';
import AbstractLargeFileUploader from '../../utils/upload/LargeFileUploader/AbstractLargeFileUploader';
import { type Client as GraphClient } from '@microsoft/microsoft-graph-client';

type CreateUploadSessionResponse = {
  uploadUrl: string;
  expirationDateTime: string;
};

export default class OneDriveLargeFileUploader extends AbstractLargeFileUploader<OneDriveFile> {
  // A bit less than 10 MB. Must be amultiple of 320 KB (320 * 1024 bytes) for
  // the OneDrive resumable upload to work
  protected readonly CHUNK_SIZE = 30 * 320 * 1024;

  constructor(private readonly graphClient: GraphClient) {
    super();
  }

  protected async startUploadSession(file: File): Promise<string> {
    const uploadSessionResponse: CreateUploadSessionResponse =
      await this.graphClient
        .api(`/me/drive/items/root:/${file.name}:/createUploadSession`)
        .post({
          item: {
            '@microsoft.graph.conflictBehavior': 'rename',
            name: file.name,
          },
        });

    return uploadSessionResponse.uploadUrl;
  }

  protected async uploadFileChunk(
    uploadChunkApiUrl: string,
    fileChunk: Blob,
    chunkByteRange: [number, number],
    totalFileSize: number,
  ): Promise<[false, unknown] | [true, OneDriveFile]> {
    const uploadResponse: OneDriveFile | unknown = await this.graphClient
      .api(uploadChunkApiUrl)
      .headers(
        this.getStandardUploadChunkRequestHeaders(
          fileChunk,
          chunkByteRange,
          totalFileSize,
        ),
      )
      .put(fileChunk);

    if (
      typeof uploadResponse !== 'object' ||
      uploadResponse === null ||
      !('id' in uploadResponse) ||
      typeof uploadResponse.id !== 'string'
    ) {
      return [false, null as unknown];
    }

    return [true, uploadResponse as OneDriveFile];
  }
}
